import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { PenIcon } from "assets/icons";
import { Button, AlertMessage } from "components/atoms";
import {
  TabbedFormToggle,
  TabConfig,
  EditTabConfig,
  DatePickerInput,
  RadioButtonGroup,
  MultiChoiceCheckboxGroup,
  InformationItem,
} from "components/molecules";
import { useUserData, useContentSanitizer } from "hooks";
import { ProfileHandling, formatDate, formatDateToString } from "services";
import { disabilityOptions } from "types";

const studentSupportSchema = Yup.object({
  hasDisabilityMedicalCondition: Yup.boolean().required(
    "Please select yes or no"
  ),
  disabilityType: Yup.array().when("hasDisabilityMedicalCondition", {
    is: true,
    then: (schema) =>
      schema.min(1, "Please select at least one disability type"),
    otherwise: (schema) => schema.nullable(),
  }),
  otherDetails: Yup.string().when("disabilityType", {
    is: (types: string[]) => types?.includes("other"),
    then: (schema) => schema.required("Please specify other condition"),
    otherwise: (schema) => schema.nullable(),
  }),
  disabilityStartDate: Yup.date().when("hasDisabilityMedicalCondition", {
    is: true,
    then: (schema) => schema.required("Please provide start date"),
    otherwise: (schema) => schema.nullable(),
  }),
});

const StudentSupportPage: React.FC = () => {
  const { userData, fetchUserData } = useUserData();
  const { sanitizeContent, sanitizeObject } = useContentSanitizer();
  const [editingTab, setEditingTab] = useState<string | null>(null);

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const formik = useFormik({
    initialValues: {
      hasDisabilityMedicalCondition: null as boolean | null,
      disabilityType: [] as string[],
      otherDetails: "",
      disabilityStartDate: null as Date | null,
    },
    validationSchema: studentSupportSchema,
    onSubmit: () => {},
  });

  const resetFormToUserData = () => {
    if (!userData) return;

    formik.setValues({
      hasDisabilityMedicalCondition:
        userData?.studentSupportServices?.hasDisabilityMedicalCondition ?? null,
      disabilityType: userData?.studentSupportServices?.disabilityType || [],
      otherDetails: userData?.studentSupportServices?.otherDetails || "",
      disabilityStartDate: userData?.studentSupportServices?.disabilityStartDate
        ? new Date(userData.studentSupportServices.disabilityStartDate)
        : null,
    });
  };

  const getFormValue = (key: string) =>
    formik.values[key as keyof typeof formik.values];

  const isComplete = () => {
    const hasDisabilityMedicalCondition = getFormValue(
      "hasDisabilityMedicalCondition"
    );

    if (hasDisabilityMedicalCondition === null) {
      return false;
    }

    if (hasDisabilityMedicalCondition === true) {
      const disabilityTypes = getFormValue("disabilityType") as string[];
      const startDate = getFormValue("disabilityStartDate");
      const hasOtherDetails = disabilityTypes.includes("other")
        ? !!getFormValue("otherDetails")
        : true;
      return disabilityTypes.length > 0 && !!startDate && hasOtherDetails;
    }

    return true;
  };

  const validateFields = async (fields: string[]): Promise<boolean> => {
    const allErrors = await formik.validateForm();
    const relevantErrors = Object.keys(allErrors).filter((key) =>
      fields.includes(key)
    );
    if (relevantErrors.length > 0) {
      const touchedFields = fields.reduce(
        (acc: { [key: string]: boolean }, field: string) => ({
          ...acc,
          [field]: true,
        }),
        {}
      );
      formik.setTouched(touchedFields);
      setErrorMessage("Please fix the validation errors before saving.");
      return false;
    }
    return true;
  };

  const saveStudentSupport = async (): Promise<boolean> => {
    try {
      const fields = [
        "hasDisabilityMedicalCondition",
        "disabilityType",
        "otherDetails",
        "disabilityStartDate",
      ];
      if (!(await validateFields(fields))) return false;

      const input = {
        id: userData!.id,
        studentSupportServices: {
          hasDisabilityMedicalCondition: getFormValue(
            "hasDisabilityMedicalCondition"
          ),
          disabilityType: sanitizeObject(
            getFormValue("disabilityType") as string[]
          ),
          otherDetails: sanitizeContent(getFormValue("otherDetails") as string),
          disabilityStartDate: getFormValue("disabilityStartDate")
            ? formatDate(getFormValue("disabilityStartDate") as Date)
            : null,
        },
      };

      await ProfileHandling.updateUser(input);
      await fetchUserData();
      setSuccessMessage("Student support services updated successfully.");
      setErrorMessage("");
      return true;
    } catch (error) {
      setErrorMessage("Failed to update student support services.");
      setSuccessMessage("");
      return false;
    }
  };

  useEffect(() => {
    if (!userData) return;

    formik.setValues({
      hasDisabilityMedicalCondition:
        userData?.studentSupportServices?.hasDisabilityMedicalCondition ?? null,
      disabilityType: userData?.studentSupportServices?.disabilityType || [],
      otherDetails: userData?.studentSupportServices?.otherDetails || "",
      disabilityStartDate: userData?.studentSupportServices?.disabilityStartDate
        ? new Date(userData.studentSupportServices.disabilityStartDate)
        : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  const displayTabs: TabConfig[] = [
    {
      id: "student-support",
      label: "Support Overview",
      description: "Learning difficulties and disability support",
      component: (
        <div className="form-section">
          <div className="section-header">
            <h4>Support Overview</h4>
            <Button
              text={<PenIcon />}
              onClick={() => setEditingTab("student-support")}
              variant="borderless"
            />
          </div>
          <dl>
            <InformationItem
              label="Learning difficulties, disability, impairment or medical condition requiring support:"
              value={
                getFormValue("hasDisabilityMedicalCondition") === null
                  ? "Not Provided"
                  : getFormValue("hasDisabilityMedicalCondition")
                    ? "Yes"
                    : "No"
              }
            />
            {getFormValue("hasDisabilityMedicalCondition") && (
              <>
                <InformationItem
                  label="Disability Types:"
                  value={
                    (getFormValue("disabilityType") as string[])?.length > 0 ? (
                      <ol>
                        {(getFormValue("disabilityType") as string[]).map(
                          (type) => (
                            <li key={type}>
                              {disabilityOptions.find(
                                (option) => option.value === type
                              )?.label || type}
                            </li>
                          )
                        )}
                      </ol>
                    ) : (
                      "Not Provided"
                    )
                  }
                />
                <InformationItem
                  label="Start Date:"
                  value={
                    getFormValue("disabilityStartDate")
                      ? formatDateToString(
                          getFormValue("disabilityStartDate") as Date
                        )
                      : "Not Provided"
                  }
                />
              </>
            )}
          </dl>
        </div>
      ),
    },
  ];

  const editTabConfigs: EditTabConfig[] = [
    {
      id: "student-support",
      label: "Support Overview",
      title: "Edit Support Information",
      onSave: saveStudentSupport,
      onCancel: resetFormToUserData,
      saveDisabled: !isComplete(),
      formFields: (
        <>
          <RadioButtonGroup
            name="hasDisabilityMedicalCondition"
            label="Do you have any learning difficulties, disability, impairment or long-term medical condition that may affect your studies or require additional assistance? *"
            value={getFormValue("hasDisabilityMedicalCondition") as boolean}
            onChange={(value) =>
              formik.setFieldValue("hasDisabilityMedicalCondition", value)
            }
          />
          {getFormValue("hasDisabilityMedicalCondition") && (
            <>
              <MultiChoiceCheckboxGroup
                name="disabilityType"
                label="Please identify the nature of your condition"
                options={disabilityOptions}
                value={getFormValue("disabilityType") as string[]}
                otherValue={getFormValue("otherDetails") as string}
                onChange={(values) =>
                  formik.setFieldValue("disabilityType", values)
                }
                onOtherChange={(value) =>
                  formik.setFieldValue("otherDetails", value)
                }
                showOther={true}
                otherPlaceholder="Please specify"
                required={true}
              />
              <DatePickerInput
                id="disabilityStartDate"
                className="disabilityStartDate"
                label="Start Date *"
                value={getFormValue("disabilityStartDate") as Date | null}
                onChange={(date) =>
                  formik.setFieldValue("disabilityStartDate", date)
                }
              />
            </>
          )}
        </>
      ),
    },
  ];

  return (
    <div className="form-group student-support-services-page">
      {successMessage && (
        <AlertMessage type="success">{successMessage}</AlertMessage>
      )}
      {errorMessage && <AlertMessage type="error">{errorMessage}</AlertMessage>}
      <TabbedFormToggle
        headerComponent={
          <div className="form-group header">
            <h2>Student Support</h2>
          </div>
        }
        displayTabs={displayTabs}
        editTabConfigs={editTabConfigs}
        formikInstance={formik}
        editingTab={editingTab}
        onTabEditToggle={setEditingTab}
      />
    </div>
  );
};

export default StudentSupportPage;
